import React, { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { strings } from "../../Translations/strings";
import "../Auth.scss";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import InputError from "../../Common/Error/InputError";
import { submitNewPassword } from "./lib";

const NewPasswordForm = ({ token, onSuccessChange }) => {
  const [resetInProgress, setResetInProgress] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState(null);
  const navigation = useNavigate();

  useEffect(() => {
    if (onSuccessChange) {
      onSuccessChange(resetSuccess);
    }
  }, [resetSuccess, onSuccessChange]);

  const schema = yup.object().shape({
    password: yup
      .string()
      .min(8, strings.validation_field_password)
      .required(strings.validation_field_password_required),
    passwordConfirm: yup
      .string()
      .oneOf([yup.ref("password")], strings.password_mismatch_error)
      .required(strings.validation_field_password_required),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      password: "",
      passwordConfirm: "",
    },
  });

  const getErrorMessage = (error) => {
    const errorMessage = error?.response?.data?.message;

    // Check for specific backend error messages
    if (errorMessage) {
      switch (errorMessage) {
        case "Password reset request does not exist":
          return strings.reset_password_error_request_not_exist;
        case "User not found":
          return strings.reset_password_error_user_not_found;
        case "Password reset request link has expired":
          return strings.reset_password_error_link_expired;
        case "Password reset link no longer valid":
          return strings.reset_password_error_link_used;
        case "Password must have more than 6 characters":
          return strings.reset_password_error_password_length;
        default:
          return strings.reset_password_error_generic_reset;
      }
    }

    // Fallback to status code based errors
    const status = error?.response?.status;
    switch (status) {
      case 400:
        return strings.reset_password_error_generic_reset;
      case 500:
        return strings.reset_password_error_generic_reset;
      case 503:
        return strings.reset_password_error_generic_reset;
      default:
        return strings.reset_password_error_generic_reset;
    }
  };

  const onSubmit = async (data) => {
    setResetInProgress(true);
    setResetError(null);

    try {
      await submitNewPassword(data.password, data.passwordConfirm, token);
      setResetInProgress(false);
      setResetSuccess(true);
    } catch (err) {
      setResetInProgress(false);
      setResetError(getErrorMessage(err));
    }
  };

  const errorStyle = {
    display: "block",
    marginTop: "0px !important",
    textAlign: "center",
  };

  if (resetSuccess) {
    return (
      <div className="reset-success-container">
        <h3 className="reset-success-title">
          {strings.reset_password_success_reset_title}
        </h3>
        <p className="reset-success-message">
          {strings.reset_password_success_reset_message}
        </p>
        <Button
          variant="primary"
          className="submit-form-register"
          onClick={() => navigation("/login")}
        >
          Prijavi se
        </Button>
      </div>
    );
  }

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Group className="mb-3 required" controlId="formBasicPassword">
          <Form.Label aria-required className="control-label">
            {strings.new_password_label}
          </Form.Label>
          <Form.Control
            type="password"
            isInvalid={errors?.password?.message}
            onKeyPress={(e) => {
              if (e.code === "Space" || e.keyCode === 32) {
                e.preventDefault();
              }
            }}
            placeholder={strings.new_password_placeholder}
            {...register("password", { required: true })}
          />
          <Form.Control.Feedback type="invalid">
            {errors?.password?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group
          className="mb-3 required"
          controlId="formBasicpasswordConfirm"
        >
          <Form.Label aria-required className="control-label">
            {strings.confirm_password_label}
          </Form.Label>
          <Form.Control
            type="password"
            isInvalid={errors?.passwordConfirm?.message}
            onKeyPress={(e) => {
              if (e.code === "Space" || e.keyCode === 32) {
                e.preventDefault();
              }
            }}
            placeholder={strings.confirm_password_placeholder}
            {...register("passwordConfirm", { required: true })}
          />
          <Form.Control.Feedback type="invalid">
            {errors?.passwordConfirm?.message}
          </Form.Control.Feedback>
        </Form.Group>

        {resetError && <InputError message={resetError} style={errorStyle} />}

        <Button
          variant="primary"
          className="submit-form-register"
          type="submit"
        >
          {resetInProgress ? (
            <div className="spinner-border text-light" role="status"></div>
          ) : (
            strings.reset_password_submit_label
          )}
        </Button>
      </Form>
    </>
  );
};

export default NewPasswordForm;
